<template>
  <div>
    <notification />
    <div class="login-section-wrapper content-wrapper">
      <progress-bar
        :currentStep="2"
        :overall-steps="progressBarOverallSteps"
      ></progress-bar>
      <div class="login-form-wrapper">
        <component
          :is="bankComponent"
          :is-show-captcha="isShowCaptcha"
          :request="request"
          :bank="bank"
          @closeErrorMessage="closeNotification"
          @setError="setCapitecError"
          @proceed="sendLoginInstruction"
        ></component>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import NotificationEvent from '@/events/notificationEvents'
import Constants from '@/constants'
import ProgressBar from '@/components/ProgressBar'
import Notification from '@/components/Notification'
import { blockBackNavigationMixin } from '@/mixins/blockBackNavigationMixin'
import { isCapitecPayAcquirer, isNotUndefined } from '@/utils'
import { isNedbankPayAcquirer } from '../utils'

export default {
  name: 'Login',
  components: {
    ProgressBar,
    Notification,
  },
  mixins: [blockBackNavigationMixin],

  props: {
    request: {
      type: Object,
      default: () => {
        // Default value thats populated with the request being sent from backend
      },
    },
    instruction: {
      type: Object,
      default: () => {
        // Default value thats populated with the instruction being sent from backend
      },
    },
  },
  data() {
    return {
      currentStep: 2,
      progressBarOverallSteps: 4,
    }
  },
  created() {
    this.checkForScraperMessages()
    this.checkForCapitecMessages()
  },
  beforeRouteLeave(to, from, next) {
    const { name } = to
    if (name === 'progressLoading') {
      next(false)
    }
    next()
  },
  computed: {
    ...mapState('transaction', ['transaction']),
    ...mapGetters('merchantSettings', ['banksList']),
    isShowCaptcha() {
      return this.request.requestType === Constants.requestTypes.CAPTCHA
    },
    transactionId() {
      var theTransactionId = this.$route.query.transactionId
      if (theTransactionId == null) {
        const queryString = new URLSearchParams(this.$route.query).toString()
        this.$logInfo({
          message: `Null value when retrieving transactionId from route.query '${queryString}'.
          Attempting to use stored transaction.`,
        })
        //try set from the transaction
        theTransactionId = this.transaction?.transactionId
      }

      if (theTransactionId == null) {
        theTransactionId = ''
        this.$logInfo({
          message:
            'Null value when retrieving transactionId from stored transaction.',
        })
      }
      return theTransactionId
    },
    bank() {
      return this.banksList.find(
        (item) => item.acquirerCode === this.transaction.acquirerCode
      )
    },
    bankComponent() {
      return (
        Constants.banks.find(
          ({ acquirerCode }) => acquirerCode === this.transaction.acquirerCode
        )?.component ?? null
      )
    },
  },
  watch: {
    'transaction.acquirerCode': {
      immediate: true,
      handler() {
        const notImplemented = [
          'sidtestcancelled',
          'sidtestcompleted',
          'SNDABS20126',
        ]

        if (notImplemented.includes(this.transaction.acquirerCode)) {
          const error = new Error(
            `Login view for bankCode ${this.transaction.acquirerCode} not implemented.`
          )
          this.$handleException({ error })
        }

        if (!this.bankComponent) {
          const error = new Error(
            `Could not set Bank Login with bankCode ${this.transaction.acquirerCode}.`
          )
          this.$handleException({ error })
        }

        if (isNedbankPayAcquirer(this.transaction.acquirerCode)) {
          this.progressBarOverallSteps = 3
        }
      },
    },
  },
  methods: {
    sendLoginInstruction({ username, password, pin, captcha }) {
      let logMessage = Constants.logMessages.enteredLoginAndCaptcha
      if (!captcha) {
        logMessage = Constants.logMessages.enteredLogin
      }

      this.$logInfo({
        transactionId: this.transactionId,
        message: logMessage,
      })

      const instruction = {
        Username: username,
        Password: password,
        Pin: pin,
        Captcha: captcha,
      }
      this.$emit('send-response', {
        messageId: this.request.messageId,
        instruction,
      })
    },
    checkForScraperMessages() {
      if (this.request.requestType === Constants.requestTypes.CAPTCHA) {
        if (
          typeof this.request.options === 'string' &&
          this.request.options.substring(0, 10) === 'data:image'
        ) {
          NotificationEvent.setNotification(
            Constants.logMessages.captchaMessage
          )
        } else {
          this.$logInfo({
            transactionId: this.transactionId,
            message: `getNotificationText QR Code Issue: ${JSON.stringify(
              this.request
            )}`,
          })
        }
      } else {
        if (isNotUndefined(this.request.options))
          NotificationEvent.setNotification(this.request.options, 'err-msg')
      }
    },

    checkForCapitecMessages() {
      if (isCapitecPayAcquirer(this.transaction.acquirerCode)) {
        this.setCapitecError(this.request.message)
      }
    },
    setCapitecError(error) {
      NotificationEvent.setNotification(error, 'err-msg')
    },
    closeNotification() {
      NotificationEvent.closeNotification()
    },
  },
}
</script>

<style scoped lang="scss">
.login-section-wrapper {
  display: grid;
  place-items: center;
  height: 80%;
  padding: 0 20px 20px 20px;

  @media screen and (max-width: $mobile-resolution) {
    padding: 0;
  }
}
</style>
